/**
 * The stand-in rig: one tinted capsule.
 *
 * Two jobs, both real:
 *
 * 1. **LOD.** Past the twelve nearest ghosts, `GhostManager` swaps the full
 *    skeleton for this. At 40 m a windmilling ribcage is four pixels of noise;
 *    a coloured capsule with a name over it carries the same information for
 *    one draw call.
 * 2. **Bootstrapping.** Chunk D is built before chunk B's `createSkeleton`
 *    exists, so this doubles as the rig factory until integration swaps it in.
 *
 * Geometry is shared across every instance and is *never* disposed — the rig
 * contract is explicit that `dispose` releases per-instance resources only.
 */

import {
  CapsuleGeometry,
  Color,
  Group,
  Mesh,
  MeshLambertMaterial,
  type BufferGeometry,
} from 'three';
import { BONE_COLOR } from '@shared/constants';
import type { SkeletonPose, SkeletonRig } from './rigTypes';
import { createNameTag, type NameTag } from './nametag';

/** Roughly the silhouette of a skeleton on a stick: ~1.8 m tall, 0.8 m wide. */
const RADIUS = 0.4;
const MID_LENGTH = 1.0;

/** How far the bone colour is pulled toward the player hue — mirrors `skeleton.ts`. */
const TINT_MIX = 0.45;

/** Name tags float here, level with the skull. */
const TAG_HEIGHT = 2.2;

let sharedGeometry: BufferGeometry | null = null;

/** Built on first use so importing this module stays free in a headless context. */
function geometry(): BufferGeometry {
  sharedGeometry ??= new CapsuleGeometry(RADIUS, MID_LENGTH, 4, 10);
  return sharedGeometry;
}

/**
 * Build a fallback rig tinted with a player's hue.
 *
 * `applyPose` honours position and yaw only. Everything else in the pose —
 * compression, charge, flail — is deliberately ignored: at the distance this rig
 * is used, none of it is visible, and pretending otherwise would cost frames for
 * nothing.
 */
export function createFallbackRig(tint: number): SkeletonRig {
  const color = new Color(BONE_COLOR).lerp(new Color(tint), TINT_MIX);
  const material = new MeshLambertMaterial({ color, flatShading: true });

  const mesh = new Mesh(geometry(), material);
  mesh.castShadow = false;
  mesh.receiveShadow = false;

  const root = new Group();
  root.name = 'ghost-fallback';
  root.add(mesh);

  let tag: NameTag | null = null;
  let tagName: string | null = null;
  let opacity = 1;
  let disposed = false;

  return {
    root,

    applyPose(pose: SkeletonPose): void {
      root.position.copy(pose.position);
      root.rotation.y = pose.yaw;
    },

    setNameTag(name: string | null): void {
      if (disposed || name === tagName) return;
      tag?.dispose();
      tag = null;
      tagName = name;
      if (name === null) return;
      tag = createNameTag(name);
      tag.sprite.position.set(0, TAG_HEIGHT, 0);
      tag.setOpacity(opacity);
      root.add(tag.sprite);
    },

    setOpacity(o: number): void {
      if (disposed) return;
      opacity = o < 0 ? 0 : o > 1 ? 1 : o;
      // Only pay for blending when actually translucent.
      const wantTransparent = opacity < 0.999;
      if (material.transparent !== wantTransparent) {
        material.transparent = wantTransparent;
        material.needsUpdate = true;
      }
      material.opacity = opacity;
      const visible = opacity > 0.01;
      if (mesh.visible !== visible) mesh.visible = visible;
      tag?.setOpacity(opacity);
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      tag?.dispose();
      tag = null;
      material.dispose(); // per-instance clone; the geometry is shared and stays.
      root.removeFromParent();
      root.clear();
    },
  };
}
